/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Autocomplete,
  TextField,
  Grid,
  Typography,
  Alert,
  Button,
  FormControl,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { notificationActions } from 'modules/common/notifications/slice';
import { useEffect, useState, useRef } from 'react';
import { X } from 'react-feather';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { isEmpty } from 'modules/common/helpers/object';
import { usersActions } from 'modules/users/slice';
import { Formik } from 'formik';
import { hotelMetadataValidation } from 'modules/users/validation/add-user-form-validation';
import ERROR_TYPES from 'modules/common/constants/error-types';
import OutlinedDiv from './outer-card';

const FormStyles = { borderRadius: 10 };
/**
 * View hotel meta data modal
 * @param {Boolean} open - To open/ close dialog box
 * @param {Function} onClose - Function to trigger on close event
 * @param {Function} selectedHotelName - selected single hotel name
 * @returns
 */
const ViewHotelMetadata = ({ open, hotelList, onClose, selectedHotelName = null }) => {
  const dispatch = useDispatch();
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  const user = useSelector(selectAuthUser);
  //
  const [hotel, setHotel] = useState(null);
  const [initialValues, setInitialValues] = useState({
    id: '',
    name: '',
    roomCapacity: '',
    timezone: '',
    chainName: '',
    displayName: '',
    city: '',
    state: '',
    country: '',
    // hotelType: '',
    chainId: '',
    hotelCode: '',
    ibe: '',
    interface: '',
    phram: '',
    phrdnName: '',
    pms: '',
    postalCode: '',
    propertyLatitude: '',
    propertyLongitude: '',
    streetAddress: '',
  });
  const formElement = useRef(null);
  // reset notifications on first render
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);
  // get only changed values
  const getChangedValues = (values, initials) =>
    Object.entries(values).reduce((acc, [key, value]) => {
      const hasChanged = initials[key] !== value;
      if (hasChanged) {
        acc[key] = value;
      }
      return acc;
    }, {});
  //
  const onSubmitUpdateHotel = async (values, { setSubmitting }) => {
    const onlyUpdatedValues = getChangedValues(values, initialValues);
    if (!isEmpty(onlyUpdatedValues)) {
      const valueObj = {
        organizationId,
        userId: user?.id,
        values: {
          hotelName: values.name,
          hotelId: values.id,
          ...onlyUpdatedValues,
        },
      };
      //
      dispatch(usersActions.updateHotelMetadata(valueObj));
    }
    setSubmitting(false);
    onClose();
  };
  //
  useEffect(() => {
    if (selectedHotelName !== null) {
      setHotel(selectedHotelName);
    }
  }, [selectedHotelName, open]);
  //
  useEffect(() => {
    if (hotel !== null) {
      setInitialValues({
        id: hotel?.id,
        name: hotel?.label,
        roomCapacity: hotel?.roomCapacity,
        databricksHotelId: hotel?.databricksHotelId,
        displayName: hotel?.altName,
        timezone: hotel?.timezone,
        chainName: hotel?.chainName,
        city: hotel?.city,
        state: hotel?.state,
        country: hotel?.country,
        // hotelType: hotel?.hotelType,
        chainId: hotel?.chainId,
        hotelCode: hotel?.hotelCode,
        ibe: hotel?.ibe,
        interface: hotel?.interface,
        phram: hotel?.phram,
        phrdnName: hotel?.phrdnName,
        pms: hotel?.pms,
        postalCode: hotel?.postalCode,
        propertyLatitude: hotel?.propertyLatitude,
        propertyLongitude: hotel?.propertyLongitude,
        streetAddress: hotel?.streetAddress,
      });
    }
  }, [hotel]);
  //
  return (
    <Dialog
      open={open}
      onClose={() => {
        setHotel(null);
        onClose();
      }}
      aria-labelledby="form-dialog-title"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
          },
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 2 }}>
        <Grid container item flexDirection="row" justifyContent="space-between" xs={12}>
          <Grid item xs={3.5}>
            <Typography variant="h5">Hotel Metadata</Typography>
          </Grid>
          <Grid item xs={3.5}>
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => {
                setHotel(null);
                onClose();
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <X />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={hotelMetadataValidation}
          onSubmit={onSubmitUpdateHotel}
        >
          {({
            handleBlur,
            handleChange,
            handleSubmit,
            values,
            isSubmitting,
            dirty,
            isValid,
            touched,
            errors,
            setFieldValue,
            // resetForm
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={2} maxHeight={400} sx={{ overflow: 'auto' }}>
                {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                  <Alert mt={2} mb={3} severity={notification?.type}>
                    {notification?.message}
                  </Alert>
                )}
                <Grid container justifyContent="space-around" alignItems="center" my={2}>
                  <Autocomplete
                    fullWidth
                    sx={{
                      mb: 2,
                      mt: 2,
                    }}
                    options={hotelList ?? []}
                    getOptionLabel={(option) => (option.label ? option.label : '')}
                    isOptionEqualToValue={(option, value) =>
                      value !== null && option.id === value.id
                    }
                    filterSelectedOptions
                    value={hotel}
                    onChange={(event, value) => {
                      setHotel(value);
                    }}
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        label="Select Hotel"
                        size="small"
                        color="primary"
                      />
                    )}
                  />
                </Grid>
                <OutlinedDiv label="Hotel Details">
                  <br />
                  <Grid
                    container
                    justifyContent="space-around"
                    alignItems="center"
                    px={4}
                    mb={2}
                    mt={6}
                  >
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        fullWidth
                        name="name"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('name', trimmedValue);
                          handleBlur(e);
                        }}
                        my={2}
                        sx={{ borderRadius: 10 }}
                        value={values.name}
                        label="Name"
                        variant="outlined"
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.name?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="displayName"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('displayName', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.displayName && errors.displayName)}
                        helperText={touched.displayName && errors.displayName}
                        value={values.displayName}
                        label="Display Name"
                        variant="outlined"
                        size="large"
                        disabled
                        InputLabelProps={{ shrink: values.displayName?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="chainId"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('chainId', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.chainId && errors.chainId)}
                        helperText={touched.chainId && errors.chainId}
                        value={values.chainId}
                        label="Chain Id"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.chainId?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="chainName"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('chainName', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.chainName && errors.chainName)}
                        helperText={touched.chainName && errors.chainName}
                        value={values.chainName}
                        label="Chain Name"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.chainName?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        type="number"
                        name="roomCapacity"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.roomCapacity ? values.roomCapacity : 0}
                        label="Room Capacity"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: true }}
                      />
                    </FormControl>
                  </Grid>
                  {/* <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        size="large"
                        select
                        name="localCurrency"
                        value={values.localCurrency}
                        label="Local Currency"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        disabled={hotel === null}
                      >
                        {removeDuplicates(currencyData)?.map((type) => (
                          <MenuItem key={`${type.currencyCode}`} value={type.currencyCode}>
                            {type.currencyCode}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  </Grid> */}
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="timezone"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('timezone', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.timezone && errors.timezone)}
                        helperText={touched.timezone && errors.timezone}
                        value={values.timezone}
                        label="Timezone"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.timezone?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="streetAddress"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('streetAddress', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.streetAddress && errors.streetAddress)}
                        helperText={touched.streetAddress && errors.streetAddress}
                        value={values.streetAddress}
                        label="Street Address"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.streetAddress?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="city"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('city', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.city}
                        error={Boolean(touched.city && errors.city)}
                        helperText={touched.city && errors.city}
                        label="City"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.city?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="state"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('state', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.state}
                        error={Boolean(touched.state && errors.state)}
                        helperText={touched.state && errors.state}
                        label="State"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.state?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="postalCode"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('postalCode', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.postalCode}
                        error={Boolean(touched.postalCode && errors.postalCode)}
                        helperText={touched.postalCode && errors.postalCode}
                        label="Postal Code"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.postalCode?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="country"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('country', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.country}
                        error={Boolean(touched.country && errors.country)}
                        helperText={touched.country && errors.country}
                        label="Country"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.country?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  {/* <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                  <FormControl sx={FormStyles} fullWidth>
                    <TextField
                      name="hotelType"
                      onChange={handleChange}
                      onBlur={(e) => {
                        const trimmedValue = e.target.value.trim();
                        setFieldValue('hotelType', trimmedValue);
                        handleBlur(e);
                      }}
                      value={values.hotelType}
                      label="Hotel Type"
                      variant="outlined"
                      size="large"
                      disabled={hotel === null}
                    />
                  </FormControl>
                </Grid> */}
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="hotelCode"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('hotelCode', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.hotelCode}
                        error={Boolean(touched.hotelCode && errors.hotelCode)}
                        helperText={touched.hotelCode && errors.hotelCode}
                        label="Hotel Code"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.hotelCode?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="ibe"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('ibe', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.ibe}
                        error={Boolean(touched.ibe && errors.ibe)}
                        helperText={touched.ibe && errors.ibe}
                        label="IBE"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.ibe?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="interface"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('interface', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.interface && errors.interface)}
                        helperText={touched.interface && errors.interface}
                        value={values.interface}
                        label="Interface"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.interface?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="phram"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('phram', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.phram && errors.phram)}
                        helperText={touched.phram && errors.phram}
                        value={values.phram}
                        label="PHRAM"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.phram?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="phrdnName"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('phrdnName', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.phrdnName && errors.phrdnName)}
                        helperText={touched.phrdnName && errors.phrdnName}
                        value={values.phrdnName}
                        label="PHRDN Name"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.phrdnName?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="pms"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('pms', trimmedValue);
                          handleBlur(e);
                        }}
                        value={values.pms}
                        error={Boolean(touched.pms && errors.pms)}
                        helperText={touched.pms && errors.pms}
                        label="PMS"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.pms?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="propertyLatitude"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('propertyLatitude', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.propertyLatitude && errors.propertyLatitude)}
                        helperText={touched.propertyLatitude && errors.propertyLatitude}
                        value={values.propertyLatitude}
                        label="Property Latitude"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.propertyLatitude?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                    <FormControl sx={FormStyles} fullWidth>
                      <TextField
                        name="propertyLongitude"
                        onChange={handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          setFieldValue('propertyLongitude', trimmedValue);
                          handleBlur(e);
                        }}
                        error={Boolean(touched.propertyLongitude && errors.propertyLongitude)}
                        helperText={touched.propertyLongitude && errors.propertyLongitude}
                        value={values.propertyLongitude}
                        label="Property Longitude"
                        variant="outlined"
                        size="large"
                        disabled={hotel === null}
                        InputLabelProps={{ shrink: values.propertyLongitude?.length > 0 }}
                      />
                    </FormControl>
                  </Grid>
                </OutlinedDiv>
              </Grid>

              <input ref={formElement} style={{ visibility: 'hidden' }} type="submit" />
              <Grid container direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setHotel(null);
                    onClose();
                  }}
                  sx={{ marginLeft: 2, marginRight: 4 }}
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={isSubmitting || hotel === null || !dirty || !isValid}
                >
                  SUBMIT
                </Button>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default ViewHotelMetadata;
